import type { TableId } from "@/app/Common/Utils/table-id";

import { useWaitlistActions } from "@/app/Waitlists/Actions/useWaitlistActions";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import {
  WaitlistListItem,
  waitlistListQuery,
} from "@/app/Waitlists/GraphQL/waitlistListQuery";
import { formatDate } from "@/lib/Formatters/formatDate";
import { ResultOf, VariablesOf } from "@graphql-typed-document-node/core";
import {
  OrderByRelationWithColumnAggregateFunction,
  QueryWaitlistsOrderColumn,
  QueryWaitlistsOrderRelationOrderByClause,
  QueryWaitlistsOrderUserColumn,
  SortOrder,
  WaitlistListQueryVariables,
} from "@/gql/graphql";
import { CellContext } from "@/lib/Components/DataTable/types";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { UserActivityDialog } from "@/app/Users/Components/UserActivityDialog";
import { PhoneIcon } from "lucide-react";
import { UserProfilePicture } from "@/app/Users/Components/UserProfilePicture";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { Button } from "@/components/catalyst/button";
import { WaitlistMatchesCell } from "@/app/Waitlists/Components/Tables/WaitlistMatchesCell";
import { ReactNode, useState } from "react";
import { useTranslations } from "use-intl";
import { SelectFilter } from "@/lib/Components/DataTable/Filters/SelectFilter";

type WaitlistColumnId =
  | "customer"
  | "trip"
  | "dates"
  | "matches"
  | "last_called"
  | "created"
  | "actions";

type LastCallFilter = "24" | "48" | "72" | "168" | null;

export function WaitlistTable({
  id,
  searchable,
  rightButtons,
  title,
  queryVariables,
}: {
  id: TableId;
  queryVariables?: Partial<WaitlistListQueryVariables>;
  searchable?: boolean;
  rightButtons?: ReactNode;
  title?: string;
}) {
  const getActions = useWaitlistActions();
  const [lastCall, setLastCall] = useState<LastCallFilter>("168");

  const columns: DataTableColDef<WaitlistListItem, any, WaitlistColumnId>[] = [
    {
      id: "customer",
      accessorFn: (data) => data,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Customer" />
      ),
      cell: CustomerCell,
    },
    {
      id: "trip",
      header: "Trip",
      cell: TripCell,
    },
    {
      id: "dates",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Dates" />
      ),
      accessorFn: (row) =>
        `${formatDate(row.earliest_departure_date)} - ${formatDate(
          row.latest_departure_date,
        )}`,
    },
    {
      id: "matches",
      header: "Matches",
      cell: WaitlistMatchesCell,
    },
    {
      id: "last_called",
      header: "Last called",
      accessorFn: (row) => row.user.latestCallActivity?.created_at,
      cell: TimeSinceNowCell,
    },
    {
      id: "created",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      accessorFn: (row) => row.created_at,
      cell: TimeSinceNowCell,
    },
    {
      id: "actions",
      header: "",
      cell: WaitlistActions,
    },
  ];

  return (
    <DataTable<
      ResultOf<typeof waitlistListQuery>,
      VariablesOf<typeof waitlistListQuery>,
      WaitlistColumnId,
      WaitlistListItem
    >
      getActions={getActions}
      id={id}
      initialSorting={[{ id: "created", desc: true }]}
      title={title}
      document={waitlistListQuery}
      columns={columns}
      accessor={(data) => data.waitlists}
      getQueryVariables={({ pagination, sorting, search }) => {
        if (search) {
          return {
            search,
            first: pagination.pageSize,
            page: pagination.pageIndex,
          } satisfies WaitlistListQueryVariables;
        }

        const order = sorting
          .map((sort) => {
            const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;

            if (sort.id === "customer") {
              return {
                order,
                user: {
                  column: QueryWaitlistsOrderUserColumn.Name,
                  aggregate: OrderByRelationWithColumnAggregateFunction.Max,
                },
              } satisfies QueryWaitlistsOrderRelationOrderByClause;
            }

            if (sort.id === "created") {
              return {
                order,
                column: QueryWaitlistsOrderColumn.CreatedAt,
              } satisfies QueryWaitlistsOrderRelationOrderByClause;
            }

            if (sort.id === "dates") {
              return {
                order,
                column: QueryWaitlistsOrderColumn.EarliestDepartureDate,
              };
            }

            console.warn(`sort not configured for ${sort.id}`);

            return null;
          })
          .filter(Boolean);

        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          hrsSinceLastCall: lastCall ? +lastCall : undefined,
          order: order.length
            ? order
            : [
                {
                  order: SortOrder.Desc,
                  column: QueryWaitlistsOrderColumn.CreatedAt,
                },
              ],
          ...queryVariables,
        } satisfies WaitlistListQueryVariables;
      }}
      searchable={searchable}
      rightButtons={rightButtons}
      filters={(table) => {
        return (
          <>
            <SelectFilter
              label="Last called"
              selected={lastCall}
              onChange={(newVal) => {
                table.setPagination((prev) => ({
                  ...prev,
                  pageIndex: 0,
                }));
                setLastCall(newVal);
              }}
              options={[
                {
                  value: "24",
                  label: ">24hrs",
                },
                {
                  value: "48",
                  label: ">48hrs",
                },
                {
                  value: "72",
                  label: ">72hrs",
                },
                {
                  value: "168",
                  label: ">7 days",
                },
              ]}
            />
          </>
        );
      }}
    />
  );
}

function TripCell({ row }: CellContext<WaitlistListItem, any>) {
  const t = useTranslations("city");

  return (
    <div className="flex flex-col gap-2">
      <div>
        {row.original.departureCity
          ? row.original.departureCity.name
          : t(`state.${row.original.departure_state}`)}
      </div>

      <div>
        {row.original.deliveryCity
          ? row.original.deliveryCity.name
          : t(`state.${row.original.delivery_state}`)}
      </div>
    </div>
  );
}

function WaitlistActions({ row }: CellContext<WaitlistListItem, any>) {
  const waitlist = row.original;

  const { open } = useDialog(UserActivityDialog);

  return (
    <Button
      className="shrink-0"
      outline
      onClick={() => {
        open({
          userId: waitlist.user.id,
        });
      }}
    >
      <PhoneIcon className="size-3.5" />
      Log
    </Button>
  );
}

function CustomerCell({ row }: CellContext<WaitlistListItem, any>) {
  const user = row.original.user;

  return (
    <div className="flex items-center space-x-3">
      <UserProfilePicture model={user} />
      <div>
        <p>{user.name?.trim() || "--"}</p>
        <p>
          {user.primary_phone ? (
            <a
              className="text-blue-500 underline"
              href={`tel:${user.primary_phone}`}
            >
              {user.primary_phone}
            </a>
          ) : (
            "--"
          )}
        </p>
        <p>
          {user.primary_email ? (
            <a
              className="text-blue-500 underline"
              href={`mailto:${user.primary_email}`}
            >
              {user.primary_email}
            </a>
          ) : (
            "--"
          )}
        </p>
      </div>
    </div>
  );
}
